import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { parseAllowedModules, resolveModuleForPath, userHasModule } from '../constants/appModules';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  const login = (nextToken, userData) => {
    const normalized = {
      ...userData,
      allowed_modules: userData.role === 'staff'
        ? parseAllowedModules(userData.allowed_modules)
        : userData.allowed_modules
    };
    localStorage.setItem('token', nextToken);
    localStorage.setItem('user', JSON.stringify(normalized));
    setToken(nextToken);
    setUser(normalized);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    if (!userData) return;
    const normalized = {
      ...user,
      ...userData,
      allowed_modules: (userData.role || user?.role) === 'staff'
        ? parseAllowedModules(userData.allowed_modules ?? user?.allowed_modules)
        : (userData.allowed_modules ?? user?.allowed_modules)
    };
    localStorage.setItem('user', JSON.stringify(normalized));
    setUser(normalized);
  };

  const isAuthenticated = () => !!token && !!user;

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) return roles.includes(user.role);
    return user.role === roles;
  };

  const canAccessModule = useCallback((moduleKey) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'consultant') return true;
    if (user.role !== 'staff') return false;
    return userHasModule(user, moduleKey);
  }, [user]);

  const canAccessPath = useCallback((pathname) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'consultant') return true;
    if (user.role !== 'staff') return false;
    const moduleKey = resolveModuleForPath(pathname);
    if (!moduleKey) return true;
    return userHasModule(user, moduleKey);
  }, [user]);

  const authHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      logout,
      updateUser,
      isAuthenticated,
      hasRole,
      canAccessModule,
      canAccessPath,
      authHeaders,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
